/* KD9 — /app/mis-diagnosticos (User) — lista real desde /api/diagnostics/mine. */
import React, { useEffect, useState } from "react";
import { PageShell } from "@/pages/dashboard/RolePages";
import { Activity, Check, ClipboardList, Clock, Copy, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";

const STATUS_META = {
  draft_requirements:{ label: "Información o fotos pendientes", color: "#F59E0B" },
  available:       { label: "Disponible en la Bolsa de Diagnósticos", color: "#3C78B5" },
  assigned:        { label: "Tomado por un consultor", color: "#0F4C81" },
  in_progress:     { label: "Diagnóstico en elaboración", color: "#A855F7" },
  correction_requested:{ label: "En corrección por el consultor", color: "#F59E0B" },
  in_review:       { label: "En proceso",     color: "#A855F7" },
  worker_submitted:{ label: "En revisión", color: "#A855F7" },
  submitted:       { label: "Revisión aprobada · preparando entrega", color: "#3C78B5" },
  approved_delivered:{ label: "Aprobado y entregado", color: "#22C55E" },
  rejected:        { label: "Rechazado",      color: "#FCA5A5" },
};

const WA_REASON = {
  ready: "Número asociado, verificado y autorizado en KD9.",
  no_phone_on_account: "La cuenta titular del diagnóstico no tiene un teléfono registrado; comprueba que estés en el perfil correcto.",
  no_whatsapp_consent: "El teléfono está registrado, pero esta cuenta no tiene autorización de mensajes WhatsApp guardada.",
  phone_not_verified: "El teléfono está registrado, pero su verificación no está activa.",
  invalid_phone_format: "El teléfono guardado tiene un formato no válido.",
  account_not_found: "No se encontró la cuenta destinataria.",
};

const MisPedidos = () => {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const [waStatus, setWaStatus] = useState({});
  const [waBusy, setWaBusy] = useState("");
  const [aiStatus, setAiStatus] = useState({});
  const [aiSupplement, setAiSupplement] = useState({});
  const [aiBusy, setAiBusy] = useState("");
  const [aiSaved, setAiSaved] = useState({});
  const focusedId = searchParams.get("focus");

  const checkWhatsApp = async (id, retry = false) => {
    setWaBusy(id);
    try {
      if (retry) await api.post(`/api/diagnostics/${encodeURIComponent(id)}/whatsapp-retry`);
      const { data } = await api.get(`/api/diagnostics/${encodeURIComponent(id)}/whatsapp-status`);
      setWaStatus((previous) => ({ ...previous, [id]: { ...data, error: "" } }));
    } catch (e) {
      setWaStatus((previous) => ({ ...previous, [id]: { error: formatApiError(e) } }));
    } finally {
      setWaBusy("");
    }
  };

  const saveAiSupplement = async (id) => {
    const missing = aiStatus[id] || {};
    const inputs = aiSupplement[id] || {};
    const payload = {};
    if (missing.missing_full_name) payload.full_name = String(inputs.full_name || "").trim();
    if (missing.missing_birth_date) payload.birth_date = inputs.birth_date || "";
    setAiBusy(id);
    setError("");
    try {
      const { data } = await api.patch("/api/diagnostics/" + encodeURIComponent(id) + "/ai-supplement", payload);
      setAiStatus(previous => ({ ...previous, [id]: data }));
      setAiSaved(previous => ({ ...previous, [id]: true }));
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setAiBusy("");
    }
  };

  const copyStudy = async (row) => {
    try {
      await navigator.clipboard.writeText(row.study_content || "");
      setCopiedId(row.id);
      window.setTimeout(() => setCopiedId(""), 1600);
    } catch {
      setError("No fue posible copiar el diagnóstico. Selecciona el texto manualmente.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: diagnostics } = await api.get("/api/diagnostics/mine");
        if (!cancelled) { setRows(diagnostics); setError(""); }
        await Promise.all(diagnostics.filter(row => row.status === "available").map(async row => {
          try {
            const { data } = await api.get("/api/diagnostics/" + encodeURIComponent(row.id) + "/ai-fallback-status");
            if (!cancelled) setAiStatus(previous => ({ ...previous, [row.id]: data }));
          } catch {
            // The status endpoint is optional; keep paid orders visible.
          }
        }));
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <PageShell kicker="Usuario · Mis diagnósticos" title="MIS DIAGNÓSTICOS"
      desc="Historial y estado en tiempo real de todas tus solicitudes."
      testId="mis-diagnosticos-page">
      {error && <div className="kd9-card p-4 mb-4 text-sm font-mont" style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }} data-testid="mine-error">{error}</div>}
      {rows.length === 0 && !error && (
        <div className="kd9-card p-10 text-center font-cormorant" style={{ color: "var(--kd9-text-soft)" }} data-testid="mine-empty">
          Todavía no has solicitado ningún diagnóstico. Ve al <Link to="/app/diagnosticos" style={{ color: "var(--kd9-gold)" }}>catálogo</Link>.
        </div>
      )}
      <div className="space-y-4">
        {rows.map((r, i) => {
          const meta = STATUS_META[r.status] || { label: r.status, color: "#888" };
          return (
            <div key={r.id} className="kd9-card p-5" style={focusedId === r.id ? { borderColor: "var(--kd9-gold)", boxShadow: "0 0 0 1px rgba(212,175,55,0.25)" } : undefined} data-testid={`mine-row-${i}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 min-w-0">
                <div className="flex-1 min-w-0 w-full">
                  <div className="flex flex-wrap items-center gap-3 mb-2 min-w-0">
                    <ClipboardList size={16} style={{ color: "var(--kd9-gold)" }} />
                    <div className="font-cinzel kd9-gold-text text-base tracking-wider break-words min-w-0">{r.catalog_title || r.catalog_slug}</div>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold"
                      style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}
                      data-testid={`mine-status-${i}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-[12px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
                    <div><Clock size={11} className="inline mr-1" /> Solicitado: {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</div>
                    <div>Referencia KD9: <span style={{ color: "var(--kd9-text)" }}>{r.diagnostic_code || `KD9-DIAG-${r.id.slice(-10).toUpperCase()}`}</span></div>
                    {r.energy_cost != null && <div>Costo en energía: <span style={{ color: "var(--kd9-gold)" }}>{new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(r.energy_cost)} Energía KD9</span></div>}
                    {r.reviewed_at && <div>Revisado: {new Date(r.reviewed_at).toLocaleString()}</div>}
                    {r.delivered_at && <div>Entregado: {new Date(r.delivered_at).toLocaleString()}</div>}
                    {r.published_at && <div>Publicado para ti: {new Date(r.published_at).toLocaleString()}</div>}
                    {r.reviewed_by && <div>Consultor asignado: <span style={{ color: "var(--kd9-text)" }}>#{r.reviewed_by.slice(-6)}</span></div>}
                  </div>
                  {r.admin_notes && (
                    <p className="mt-3 font-cormorant italic text-sm" style={{ color: "#93C5FD" }} data-testid={`mine-adminobs-${i}`}>
                      Observaciones consultor: &ldquo;{r.admin_notes}&rdquo;
                    </p>
                  )}
                  {r.director_notes && (
                    <p className="mt-1 font-cormorant italic text-sm" style={{ color: "var(--kd9-gold)" }} data-testid={`mine-dirobs-${i}`}>
                      Director KD9: &ldquo;{r.director_notes}&rdquo;
                    </p>
                  )}
                  {(r.timeline || []).length > 0 && (
                    <details className="mt-4 rounded-xl border p-4" style={{ borderColor: "var(--kd9-border)", background: "rgba(255,255,255,0.02)" }} data-testid={`mine-trace-${i}`}>
                      <summary className="cursor-pointer select-none font-cinzel text-[11px] tracking-wider flex items-center gap-2">
                        <Activity size={13} style={{ color: "var(--kd9-gold)" }} /> TRAZABILIDAD KD9
                      </summary>
                      <div className="mt-4 space-y-3">
                        {(r.timeline || []).map((event, eventIndex) => (
                          <div key={`${event.type || "event"}-${eventIndex}`} className="grid grid-cols-[12px_1fr] gap-3">
                            <div className="relative">
                              <span className="absolute top-1 left-0 h-2.5 w-2.5 rounded-full" style={{ background: "var(--kd9-gold)" }} />
                              {eventIndex < (r.timeline || []).length - 1 && <span className="absolute top-4 left-[4px] bottom-[-14px] w-px" style={{ background: "var(--kd9-border)" }} />}
                            </div>
                            <div>
                              <div className="font-mont text-[11px]" style={{ color: "var(--kd9-text)" }}>{event.label || event.type}</div>
                              <div className="mt-0.5 font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>
                                {event.at ? new Date(event.at).toLocaleString() : "—"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
                <div className="flex flex-col gap-2 w-full sm:w-auto sm:shrink-0">
                  {r.status === "draft_requirements" && (
                    <Link to={`/app/diagnosticos?resume=${encodeURIComponent(r.id)}`} className="kd9-btn-outline px-3 py-3 rounded-lg text-[11px] flex justify-center items-center gap-2 whitespace-normal text-center">Continuar carga · sin pagar de nuevo</Link>
                  )}
                  {["available", "assigned", "in_progress"].includes(r.status) && (
                    <button type="button" onClick={() => checkWhatsApp(r.id)} disabled={waBusy === r.id}
                      className="kd9-btn-outline px-3 py-3 rounded-lg text-[11px] flex justify-center items-center gap-2 text-center">
                      <MessageCircle size={15}/> {waBusy === r.id ? "Comprobando…" : "Consultar aviso WhatsApp"}
                    </button>
                  )}
                </div>
              </div>
              {r.status === "available" && aiStatus[r.id] && (
                <div className="mt-3 rounded-xl border p-4 text-xs font-mont space-y-3"
                  style={{ borderColor: "var(--kd9-border)" }} data-testid={"diagnostic-ai-status-" + i}>
                  <strong className="font-cinzel kd9-gold-text">SEGUIMIENTO · DON SABIONDO</strong>
                  {aiStatus[r.id].ready_for_generation ? (
                    <p>El estudio dispone de los datos requeridos. Don Sabiondo puede tomarlo cuando cumpla las condiciones de asignación.</p>
                  ) : (aiStatus[r.id].missing_full_name || aiStatus[r.id].missing_birth_date) ? (
                    <>
                      <p>Para completar los cálculos del Diagnóstico Integral necesitamos estos datos. Tu pedido, pago y fotografías ya están guardados. <strong>No tienes que pagar otra vez.</strong></p>
                      {aiStatus[r.id].missing_full_name && (
                        <label className="block space-y-1">
                          <span>Nombre completo de nacimiento</span>
                          <input className="kd9-input p-3 w-full" type="text" autoComplete="name"
                            maxLength={160} placeholder="Nombres y apellidos"
                            value={aiSupplement[r.id]?.full_name || ""}
                            onChange={e => setAiSupplement(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), full_name: e.target.value } }))}/>
                        </label>
                      )}
                      {aiStatus[r.id].missing_birth_date && (
                        <label className="block space-y-1">
                          <span>Fecha de nacimiento</span>
                          <input className="kd9-input p-3 w-full" type="date"
                            value={aiSupplement[r.id]?.birth_date || ""}
                            onChange={e => setAiSupplement(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), birth_date: e.target.value } }))}/>
                        </label>
                      )}
                      <button type="button" className="kd9-btn-primary px-4 py-3 rounded-lg disabled:opacity-50"
                        disabled={aiBusy === r.id || (aiStatus[r.id].missing_full_name && !String(aiSupplement[r.id]?.full_name || "").trim()) || (aiStatus[r.id].missing_birth_date && !aiSupplement[r.id]?.birth_date)}
                        onClick={() => saveAiSupplement(r.id)}>
                        {aiBusy === r.id ? "Guardando…" : "Completar datos sin pagar de nuevo"}
                      </button>
                    </>
                  ) : (
                    <p>Este expediente está pendiente de validación adicional. La plataforma conservará el pedido mientras revisas sus requisitos.</p>
                  )}
                  {aiSaved[r.id] && (
                    <p style={{ color: "#86EFAC" }}>Datos guardados. Don Sabiondo volverá a evaluarlo en su siguiente ciclo, siempre que el pedido siga disponible.</p>
                  )}
                </div>
              )}
              {waStatus[r.id] && (
                <div className="mt-3 rounded-xl border p-3 text-xs font-mont space-y-2"
                  style={{borderColor:"var(--kd9-border)"}} data-testid={`diagnostic-whatsapp-status-${i}`}>
                  <strong>Estado de WhatsApp para este diagnóstico</strong>
                  {waStatus[r.id].error ? <p style={{color:"#FCA5A5"}}>{waStatus[r.id].error}</p> : (
                    <>
                      <p>{WA_REASON[waStatus[r.id].recipient_reason] || "Estado del destinatario no disponible."}</p>
                      <p>Entrega: <strong>{({
                        sent:"Enviado al proveedor WhatsApp",
                        delivered:"Entregado", read:"Leído", failed:"Fallido",
                        eligible:"Pendiente de envío", sending:"En curso",
                        not_generated:"Sin aviso registrado",
                      })[waStatus[r.id].whatsapp_delivery_status] || waStatus[r.id].whatsapp_delivery_status}</strong>
                      </p>
                      {waStatus[r.id].recipient_reason === "ready" && ["eligible", "not_generated", "skipped_no_opt_in_or_phone", "skipped_no_recipient", "skipped_no_whatsapp_consent", "skipped_phone_not_verified", "skipped_no_phone_on_account"].includes(waStatus[r.id].whatsapp_delivery_status) && (
                        <button type="button" onClick={() => checkWhatsApp(r.id, true)} disabled={waBusy === r.id}
                          className="kd9-btn-outline px-3 py-2 rounded-lg text-[11px]">
                          Reintentar aviso de este pedido
                        </button>
                      )}
                      {waStatus[r.id].recipient_reason !== "ready" && <p>Revisa los datos y permisos del teléfono en <Link to="/app/perfil" className="underline">Mi perfil</Link>. No se requiere otra compra.</p>}
                    </>
                  )}
                </div>
              )}
              {r.status === "approved_delivered" && r.study_content && (
                <section className="mt-5 w-full min-w-0 max-w-full rounded-xl border p-4 sm:p-5" style={{ borderColor: "rgba(34,197,94,0.35)", background: "rgba(34,197,94,0.05)" }} data-testid={`mine-result-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-cinzel text-xs tracking-wider" style={{ color: "#86EFAC" }}>DIAGNÓSTICO ENTREGADO</div>
                      <div className="mt-1 text-[10px] uppercase tracking-widest opacity-60">Versión final {r.delivered_revision || r.draft_revision}</div>
                    </div>
                    <button onClick={() => copyStudy(r)} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] flex items-center gap-2">
                      {copiedId === r.id ? <Check size={12}/> : <Copy size={12}/>}
                      {copiedId === r.id ? "Copiado" : "Copiar diagnóstico"}
                    </button>
                  </div>
                  <details className="mt-4">
                    <summary className="cursor-pointer select-none font-cinzel kd9-gold-text text-xs tracking-wider">VER DIAGNÓSTICO COMPLETO</summary>
                    <div className="mt-4 w-full min-w-0 max-w-full whitespace-pre-wrap break-words overflow-x-hidden font-cormorant text-base sm:text-lg leading-relaxed" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                      {r.study_content}
                    </div>
                  </details>
                </section>
              )}
            </div>
          );
        })}
      </div>
    </PageShell>
  );
};

export default MisPedidos;
